import React, { useMemo, useState } from "react";
import { Box, Popover, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useCareManagementQuery } from "../../hooks/queries/useCareManagementQuery";
import { useAppSelector } from "../../store/hooks";
import { primary } from "../../constants/color-constants";
import type { CareManagementCase } from "../../types/care-management";

const formatDisplayDate = (value?: string | null) => {
  if (!value) return "--";

  const trimmed = value.trim();
  const mdy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (mdy) return `${mdy[1]}-${mdy[2]}-${mdy[3]}`;

  const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[2]}-${ymd[3]}-${ymd[1]}`;

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;

  const pad = (input: number) => input.toString().padStart(2, "0");
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`;
};

const displayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "--";
  return String(value);
};

export const buildCairePatientDetailRows = (
  patientCase?: CareManagementCase | null
) => {
  const los =
    typeof patientCase?.lengthOfStay === "number"
      ? `${patientCase.lengthOfStay} day${
          patientCase.lengthOfStay === 1 ? "" : "s"
        }`
      : "--";

  return [
    { label: "Patient Name", value: displayValue(patientCase?.patientName) },
    { label: "Date of Birth", value: formatDisplayDate(patientCase?.dob) },
    { label: "Gender", value: displayValue(patientCase?.gender) },
    { label: "MRN #", value: displayValue(patientCase?.mrn) },
    {
      label: "Date of Admission",
      value: formatDisplayDate(patientCase?.dataOfVisit),
    },
    { label: "LOS", value: los },
    { label: "Visit ID", value: displayValue(patientCase?.fin) },
  ];
};

const PATIENT_CHIP_BG = "oklch(90.3% .076 319.62)";

const CairePatientDetailsChip: React.FC = () => {
  const [opened, setOpened] = useState(false);
  const mrn = useAppSelector((state) => state.fhir.mrn) ?? "";
  const providerNpi = useAppSelector((state) => state.provider.providerNPI);
  const { data } = useCareManagementQuery(mrn, providerNpi, {
    polling: true,
    enabled: Boolean(mrn && providerNpi),
  });

  const patientCase = data?.case;
  const patientName = displayValue(patientCase?.patientName);
  const dob = formatDisplayDate(patientCase?.dob);
  const gender = displayValue(patientCase?.gender);
  const detailRows = useMemo(
    () => buildCairePatientDetailRows(patientCase),
    [patientCase]
  );

  if (!patientCase?.patientName && !patientCase?.mrn) {
    return null;
  }

  const chipFields = [
    { label: "Patient Name", value: patientName },
    { label: "DOB", value: dob },
    { label: "Gender", value: gender },
  ];

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom"
      withArrow
      shadow="md"
      radius="md"
      offset={8}
      withinPortal
      styles={{
        dropdown: {
          border: `1px solid ${PATIENT_CHIP_BG}`,
        },
        arrow: {
          borderColor: PATIENT_CHIP_BG,
        },
      }}
    >
      <Popover.Target>
        <Box
          component="button"
          type="button"
          onClick={() => setOpened((current) => !current)}
          aria-label="Patient details"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            minWidth: 0,
            maxWidth: 520,
            padding: "6px 14px",
            borderRadius: 10,
            border: `1px solid ${PATIENT_CHIP_BG}`,
            backgroundColor: PATIENT_CHIP_BG,
            cursor: "pointer",
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          {chipFields.map((field, index) => (
            <Box
              key={field.label}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                }}
              >
                <Text size="xs" c="dimmed" fw={400} style={{ lineHeight: 1.2 }}>
                  {field.label}
                </Text>
                {index === 0 ? (
                  <IconInfoCircle size={14} color={primary[5]} aria-hidden />
                ) : null}
              </Box>
              <Text
                size="sm"
                fw={400}
                c="dark"
                style={{
                  lineHeight: 1.25,
                  maxWidth: 160,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                }}
              >
                {field.value}
              </Text>
            </Box>
          ))}
        </Box>
      </Popover.Target>

      <Popover.Dropdown
        p={0}
        style={{
          border: `1px solid ${PATIENT_CHIP_BG}`,
          minWidth: 280,
          maxWidth: 340,
        }}
      >
        <Box style={{ padding: "10px 14px" }}>
          {detailRows.map((row) => (
            <Box
              key={row.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                padding: "5px 0",
              }}
            >
              <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                {row.label}
              </Text>
              <Text
                size="sm"
                fw={600}
                ta="right"
                style={{
                  wordBreak: "break-word",
                }}
              >
                {row.value}
              </Text>
            </Box>
          ))}
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export default CairePatientDetailsChip;
